import {
  isInstantiatedFixedRateLoanSecurity,
  isInstantiatedFloatingRateLoanSecurity,
  isInstantiatedOTCForwardSecurity,
  isInstantiatedOTCOptionSecurity,
  isInstantiatedOTCRepoSecurity,
  SubProductTypeEnum,
  type Security,
} from '@talos/kyoko';
/**
 * Minimal deal row fields read by {@link validateInstantiatedDealSecurityForModify}.
 * Call sites typically pass `OTCDealData`; tests may pass only these properties.
 */
export type DealForInstantiatedSecurityModifyCheck = {
  Symbol?: string;
  /** Values outside the known OTC union hit the `default` branch (unsupported product type). */
  SubProductType?: string;
};

export type ValidateDealSecurityResult = { ok: true; security: Security } | { ok: false; message: string };

/**
 * Ensures the deal's instantiated instrument (`deal.Symbol`) is present in reference data and
 * matches the deal's `SubProductType` before opening the modify flow.
 */
export function validateInstantiatedDealSecurityForModify(
  deal: DealForInstantiatedSecurityModifyCheck,
  securitiesBySymbol: Map<string, Security>
): ValidateDealSecurityResult {
  const symbol = deal.Symbol;
  if (!symbol) {
    return { ok: false, message: 'Cannot modify deal: deal has no Symbol.' };
  }

  const security = securitiesBySymbol.get(symbol);
  if (!security) {
    return {
      ok: false,
      message: `Cannot modify deal: instrument "${symbol}" is not loaded yet. Wait for securities reference data or refresh.`,
    };
  }

  switch (deal.SubProductType) {
    case SubProductTypeEnum.OTCForward:
      return isInstantiatedOTCForwardSecurity(security)
        ? { ok: true, security }
        : {
            ok: false,
            message: 'Cannot modify deal: loaded security is not a valid OTC forward instrument.',
          };
    case SubProductTypeEnum.OTCVanillaOption:
      return isInstantiatedOTCOptionSecurity(security)
        ? { ok: true, security }
        : {
            ok: false,
            message: 'Cannot modify deal: loaded security is not a valid OTC option instrument.',
          };
    case SubProductTypeEnum.OTCFixedRateLoan:
      return isInstantiatedFixedRateLoanSecurity(security)
        ? { ok: true, security }
        : {
            ok: false,
            message: 'Cannot modify deal: loaded security is not a valid fixed-rate loan instrument.',
          };
    case SubProductTypeEnum.OTCFloatingRateLoan:
      return isInstantiatedFloatingRateLoanSecurity(security)
        ? { ok: true, security }
        : {
            ok: false,
            message: 'Cannot modify deal: loaded security is not a valid floating-rate loan instrument.',
          };
    case SubProductTypeEnum.OTCRepo:
      return isInstantiatedOTCRepoSecurity(security)
        ? { ok: true, security }
        : {
            ok: false,
            message: 'Cannot modify deal: loaded security is not a valid repo instrument.',
          };
    default:
      return { ok: false, message: 'Cannot modify deal: unsupported product type.' };
  }
}
